import type { Score } from "~/server/scores.server"

export interface AllRatings {
	tmdb_url: string
	tmdb_user_score_original: number
	tmdb_user_score_normalized_percent: number
	tmdb_user_score_rating_count: number
	imdb_url: string
	imdb_user_score_original: number
	imdb_user_score_normalized_percent: number
	imdb_user_score_rating_count: number
	metacritic_url: string
	metacritic_user_score_original: number
	metacritic_user_score_normalized_percent: number
	metacritic_user_score_rating_count: number
	metacritic_meta_score_original: number
	metacritic_meta_score_normalized_percent: number
	metacritic_meta_score_review_count: number
	rotten_tomatoes_url: string
	rotten_tomatoes_audience_score_original: number
	rotten_tomatoes_audience_score_normalized_percent: number
	rotten_tomatoes_audience_score_rating_count: number
	rotten_tomatoes_tomato_score_original: number
	rotten_tomatoes_tomato_score_normalized_percent: number
	rotten_tomatoes_tomato_score_review_count: number
	// Letterboxd scores exist for movies only, so they stay out of getRatingKeys(): that list feeds SQL that
	// runs on the show table too.
	letterboxd_url?: string | null
	letterboxd_id?: string | null
	letterboxd_user_score_original?: number | null
	letterboxd_user_score_normalized_percent?: number | null
	letterboxd_user_score_rating_count?: number | null
	goodwatch_user_score_normalized_percent: number
	goodwatch_user_score_rating_count: number
	goodwatch_official_score_normalized_percent: number
	goodwatch_official_score_review_count: number
	goodwatch_overall_score_normalized_percent: number
	goodwatch_overall_score_voting_count: number
}

export const getRatingKeys = () => {
	const keys: (keyof AllRatings)[] = [
		"tmdb_url",
		"tmdb_user_score_original",
		"tmdb_user_score_normalized_percent",
		"tmdb_user_score_rating_count",
		"imdb_url",
		"imdb_user_score_original",
		"imdb_user_score_normalized_percent",
		"imdb_user_score_rating_count",
		"metacritic_url",
		"metacritic_user_score_original",
		"metacritic_user_score_normalized_percent",
		"metacritic_user_score_rating_count",
		"metacritic_meta_score_original",
		"metacritic_meta_score_normalized_percent",
		"metacritic_meta_score_review_count",
		"rotten_tomatoes_url",
		"rotten_tomatoes_audience_score_original",
		"rotten_tomatoes_audience_score_normalized_percent",
		"rotten_tomatoes_audience_score_rating_count",
		"rotten_tomatoes_tomato_score_original",
		"rotten_tomatoes_tomato_score_normalized_percent",
		"rotten_tomatoes_tomato_score_review_count",
		"goodwatch_user_score_normalized_percent",
		"goodwatch_user_score_rating_count",
		"goodwatch_official_score_normalized_percent",
		"goodwatch_official_score_review_count",
		"goodwatch_overall_score_normalized_percent",
		"goodwatch_overall_score_voting_count",
	]
	return keys
}

const RATING_KEYS = getRatingKeys()

// Any object with some of the rating fields: details, discover results, title cards (whose missing scores are null).
// Every title card calls this, so it fills one object instead of copying a growing one per key.
export const extractRatings = (
	details: Partial<Record<keyof AllRatings, unknown>>,
) => {
	const ratings: Partial<Record<keyof AllRatings, unknown>> = {}
	for (const key of RATING_KEYS) ratings[key] = details[key]
	return ratings as AllRatings
}

// Catalog scores use zero for unrated titles. Check the raw value before rounding or coloring it.
export const hasGoodwatchScore = (percent: unknown): percent is number =>
	typeof percent === "number" && Number.isFinite(percent) && percent > 0

// A GoodWatch score (0 to 100) as shown everywhere: whole points, rounded down, so 95.6 reads 95.
export const goodwatchScoreDisplay = (percent: number): number => Math.floor(percent)

// The step of the bg-vibe-* and text-vibe-* color tokens for a GoodWatch score (0 to 100).
export const goodwatchVibeIndex = (percent: number): number =>
	Math.min(100, Math.floor(percent / 10) * 10)

export const scoreLabels = [
	"What's your score?",
	"Unwatchable",
	"Terrible",
	"Bad",
	"Weak",
	"Mediocre",
	"Decent",
	"Good",
	"Great",
	"Excellent",
	"Masterpiece",
]

/**
 * The word for a GoodWatch score (0 to 100), the same on title pages, og cards, and in Discord: rounded to the nearest
 * band, so 92 is "Excellent" and 95 is "Masterpiece". Scores under 15 are "Unwatchable". Null when there's no score
 * (null, undefined, NaN, or infinite). Members' own 1 to 10 scores index `scoreLabels` directly.
 */
export function scoreWord(score: number | null | undefined): string | null {
	if (score == null || !Number.isFinite(score)) return null
	return scoreLabels[Math.min(10, Math.max(1, Math.round(score / 10)))]
}

const vibeColors: Record<number, string> = {
	0: "#7f1d1d",
	10: "#991b1b",
	20: "#b91c1c",
	30: "#c2410c",
	40: "#d97706",
	50: "#ca8a04",
	60: "#a3a323",
	70: "#50a33d",
	80: "#25a73d",
	90: "#16b34a",
	100: "#05b724",
}

export const getVibeColorValue = (score: Score): string => {
	return vibeColors[score * 10] ?? vibeColors[50]
}

interface ScoreBgClassOptions {
	isActive?: boolean
	withDimming?: boolean
}

export const getScoreBgClass = (
	score: Score | null,
	targetScore: Score | null = null,
	options: ScoreBgClassOptions = {}
): string => {
	const { isActive = true, withDimming = false } = options
	const vibeIndex = (targetScore ?? score ?? 0) * 10
	
	if (isActive) {
		return `bg-vibe-${vibeIndex}`
	}
	
	return `bg-vibe-${(score ?? 0) * 10}${withDimming ? "/35" : ""}`
}

export const getScoreTextClass = (score: Score | null): string => {
	if (!score) return "text-gray-500"
	return `text-vibe-${score * 10}`
}

export const getScoreLabelText = (score: Score | null): string => {
	if (!score) return scoreLabels[0]
	return `${scoreLabels[score]} · ${score}`
}

/** Selected personal score: white numeral, dark score fill, bright colored inset rim. */
export const selectedScoreStyle = (score: Score) => {
	const color = getVibeColorValue(score)
	return {
		color: "#fff",
		background: `color-mix(in srgb, ${color} 42%, #181b20)`,
		boxShadow: `inset 0 0 0 2px color-mix(in srgb, ${color} 70%, white)`,
	}
}
